import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import Cookie from 'js-cookie';
import { BASE_URL } from './connection';

type ResponseType<T = any, D = any> = AxiosResponse<T, D>;

/* eslint-disable no-unused-vars */
interface Service {
    request<T = any, R = any>(
        method: string,
        url: string,
        data?: T,
        extra?: AxiosRequestConfig
    ): Promise<R>;

    // 便捷方法
    get<T = any, R = any>(url: string, params?: T, extra?: AxiosRequestConfig): Promise<R>;
    post<T = any, R = any>(url: string, data?: T, extra?: AxiosRequestConfig): Promise<R>;
    put<T = any, R = any>(url: string, data?: T, extra?: AxiosRequestConfig): Promise<R>;
    delete<T = any, R = any>(url: string, params?: T, extra?: AxiosRequestConfig): Promise<R>;
}
/* eslint-enable no-unused-vars */

const instance = axios.create({
    baseURL: BASE_URL,
    timeout: 60 * 1000
    // 允许跨域请求携带 cookie
    // withCredentials: true
});

// 请求拦截器
instance.interceptors.request.use(
    (config) => {
        const { baseURL, url } = config;

        const baseUrl = baseURL?.startsWith('http') ? baseURL : '';
        config.url = `${baseUrl}${url}`;

        // 自动添加认证token（排除登录和验证码接口）
        if (!(url || '').includes('login') && url !== '/user/captcha') {
            const token = Cookie.get('TOKEN');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }

        // 统一带上 cookie_hash
        let cookieHash = localStorage.getItem('cookie_hash');
        if (!cookieHash) {
            cookieHash = (typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('')
            ).replace(/-/g, '');
            localStorage.setItem('cookie_hash', cookieHash);
        }
        config.headers['X-Cookie-Hash'] = cookieHash;

        // 设置默认Content-Type
        if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }

        return config;
    },
    (error) => {
        console.error('请求拦截器错误:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
instance.interceptors.response.use(
    (response) => {
        const { data, status } = response;

        // 处理业务层面的认证失效
        if (`${status}` === '200' && (`${data.code}` === '403' || `${data.code}` === '401')) {
            message.warning('认证信息已过期，请重新登录');
            setTimeout(() => {
                window.location.reload();
            }, 3000);
        }

        return response.data as any;
    },
    (error) => {
        const { response } = error;

        // 处理HTTP状态码错误
        if (response?.status === 401) {
            message.error('认证失败，请重新登录');
            // 可以根据需要跳转到登录页
            // window.location.href = '/login';
        } else if (response?.status === 403) {
            message.error('权限不足');
        } else if (response?.status === 404) {
            message.error('请求的资源不存在');
        } else if (response?.status >= 500) {
            message.error('服务器内部错误，请稍后重试');
        } else if (error.code === 'ECONNABORTED') {
            message.error('请求超时，请稍后重试');
        } else {
            message.error(error.message || '网络请求失败');
        }

        console.error('网络请求错误:', error);
        return Promise.reject(error);
    }
);

const service: Service = {
    request: (method, url, data, extra) => {
        const config: AxiosRequestConfig = {
            method,
            url,
            ...extra
        };

        if (method.toLocaleUpperCase() === 'GET' || method.toLocaleUpperCase() === 'DELETE') {
            config.params = data;
        } else {
            config.data = data;
        }

        return instance(config);
    },

    // 便捷方法
    get: (url, params, extra) => {
        return service.request('GET', url, params, extra);
    },

    post: (url, data, extra) => {
        return service.request('POST', url, data, extra);
    },

    put: (url, data, extra) => {
        return service.request('PUT', url, data, extra);
    },

    delete: (url, params, extra) => {
        return service.request('DELETE', url, params, extra);
    }
};

export default service;

export type { ResponseType };
